import "./WidgetHeader.scss";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { Box, ButtonGroup } from "@mui/material";
import { IconButton, InformationalDialog, DialogButtons, InformationalDialogActionProps, StatusTagProps, StatusTag } from "@chr/eds-react";
import { useEffect, useState } from "react";
import OrdersShipmentConfigurationDrawer from "./ConfigurationDrawers/OrdersShipmentConfigurationDrawer";
import { UserWidget } from "@/common/constants/online.homepagegateway.api";
import SettingsIcon from '../../assets/img/settings-icon.svg'
import { useDispatch, useSelector } from 'react-redux';
import { deleteWidgetInit } from "@/common/actions/widgetActions";
import { AppState } from "@/react-app-env";
import { newWidgetsSessionStorageKey } from "@/common/utilities/widgetHelper";
import { WidgetId } from "@/common/constants/widgetId";
import UpdateTitleOnlyDrawer from "./ConfigurationDrawers/UpdateTitleOnlyDrawer";
import { SidePanelController } from "@chr/navisphereonline-shared-components";
import { useChrLdFlag } from "@chr/common-launchdarkly";
import TimeFrameFilterLabel from "./Modules/TimeFrameFilterLabel";

export interface WidgetHeadersProps {
  widget: UserWidget;
}

export const WidgetHeader = ({
  widget,
  }: WidgetHeadersProps) => {
  const translate = useTranslation();
  const dispatch = useDispatch();
  const [configurationModalOpen, setConfigurationModalOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const addWidgetNotification = useSelector((state: AppState) => state.widgetsState?.addWidgetNotification);       
  const additionHeaderClass = widget.widgetId === WidgetId.RecentOrders ? "hide-bottom-line" : "show-bottom-line";
  const sidePanelController = SidePanelController.getSidePanelController();
  const widgetPanelId = `homepage-widget-${widget.id}-config-drawer`;
  const enableTimeFrameFlag = useChrLdFlag('online-visibility-enable-date-range-filter');
  const showTimeFrameLabel = (widget.widgetId === WidgetId.Shipments || widget.widgetId === WidgetId.Orders) && enableTimeFrameFlag;
    
  const handleConfigurationModalToggle = () => {
    setConfigurationModalOpen(!configurationModalOpen);

    const sessionData = sessionStorage.getItem(newWidgetsSessionStorageKey);
    if (sessionData){
      const newWidgetsInSession = JSON.parse(sessionData);
      const newArr = newWidgetsInSession.filter((i:any) => i !== widget.id);
      sessionStorage.setItem(newWidgetsSessionStorageKey, JSON.stringify(newArr));
    }
  }

  useEffect(() => {
    sidePanelController.register(widgetPanelId, () => {
      setConfigurationModalOpen(false);
    });

    return () => {
      sidePanelController.unregister(widgetPanelId);
    };
  }, []);
  
  useEffect(() => {
    if (configurationModalOpen) {
      sidePanelController.closeOtherPanels(widgetPanelId);
    }
  }, [configurationModalOpen]);

  const buttons: Partial<DialogButtons<InformationalDialogActionProps>> = {
    primaryAction: {
      buttonText: translate('Yes, delete'),
      id: 'confirm-delete-action',
      type: 'button',
      onClick: () => {
        dispatch(deleteWidgetInit(widget));
        setDeleteDialogOpen(false);
      }
    },
    secondaryAction: {
      buttonText: translate('Cancel'),
      id: 'confirm-delete-cancel',
      type: 'button',
      onClick: () => {
        setDeleteDialogOpen(false);
      }
    }
  }

  const getJustAddedTag = () => {
    const sessionData = sessionStorage.getItem(newWidgetsSessionStorageKey);
    let newWidgetsInSession = []
    if (sessionData){
      newWidgetsInSession = JSON.parse(sessionData);
    }

    if (addWidgetNotification && !newWidgetsInSession.includes(addWidgetNotification.id)) {
      newWidgetsInSession.push(addWidgetNotification.id);
      sessionStorage.setItem(newWidgetsSessionStorageKey, JSON.stringify(newWidgetsInSession));
    }

    const label = translate("Just added!");
    const severity: StatusTagProps["severity"] ='positive';

    if (newWidgetsInSession.includes(widget.id)) {
      return (
        <StatusTag
          label={label}
          severity={severity}
          size="compact"
          labelSize="medium"
          icon={true}
          data-testid={`status-tag-${widget.id}`}
        />
      );
    }

    return <></>
  }
  
  const renderConfigurationDrawer = () => {
    if (widget.widgetId === WidgetId.Orders || widget.widgetId === WidgetId.Shipments) {
      return (
        <OrdersShipmentConfigurationDrawer 
          handleToggle={handleConfigurationModalToggle}
          open={configurationModalOpen}
          widget={widget}
        />
      )
    }

    if (widget.widgetId == WidgetId.RecentOrders) {
      return (
        <UpdateTitleOnlyDrawer 
          handleToggle={handleConfigurationModalToggle}
          open={configurationModalOpen}
          widget={widget}
        />
      )
    }
  }

  return (
    <Box className={additionHeaderClass}>
      <Box className={`widget-header `} style={showTimeFrameLabel ? { paddingBottom: '0px' } : {}}>
        <Box className="widget-title header-item" title={translate(widget.title)}>
          {translate(widget.title)}
        </Box>
        <Box className="header-item">
          {getJustAddedTag()}
        </Box>
        <Box className="header-item">
          <ButtonGroup className="grid-layout-drag-cancel">
            <IconButton variant="tertiary" icon='delete' aria-label='delete-widget' onClick={() => {setDeleteDialogOpen(true);}} />
            <button className='btn-settings' data-testid={`settings-${widget.id}`} onClick={handleConfigurationModalToggle}>
              <img src={SettingsIcon} />
            </button>
          </ButtonGroup>
        </Box>
        {renderConfigurationDrawer()}
        <InformationalDialog 
          title={translate('Are you sure you want to delete this widget?')}
          bodyText={translate('This widget will be removed if you click Yes, delete. You can add it again through the widget configure menu.')}
          open={deleteDialogOpen} 
          onClose={() => {
            setDeleteDialogOpen(false);
          }}
          {...buttons as any}
        />
      </Box>
      {showTimeFrameLabel && <TimeFrameFilterLabel widgetId={widget.widgetId} />}
    </Box>
  );
}

export default WidgetHeader;